import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { isNil } from 'lodash'
import { isIdPresent } from 'utils/typeUtils'
import { type RequiredId } from 'utils/typeUtils'
import { type Resource } from 'types/api'
import { SectionHeader } from 'components/SectionHeader'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'stores/useSnackbar'
import { formatDateTime } from 'utils/util'
import { EditResourceDetails } from './EditResourceDetails'
import { usePutResource } from 'hooks/api/Resources/usePutResource'
import { useGetResourceById } from 'hooks/api/Resources/useGetResourceById'
import { useGetResourceCategories } from 'hooks/api/Resources/useGetResourceCategories'

export interface EditResourceDetailsProps {
  resource: RequiredId<Resource>
  onSubmitResource: (resource: RequiredId<Resource>) => void
  toggleModalIsOpen: () => void
  updateIsActive: boolean
  isModalOpen: boolean
}

export const ResourcesDetailsSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const toggleModalIsOpen = () => setIsModalOpen(!isModalOpen)
  const id = Number(useParams().id)
  const getResourceQuery = useGetResourceById(id)
  const getResourceCategoriesQuery = useGetResourceCategories()
  const queryClient = useQueryClient()
  const setMessage = useSnackbar((state) => state.setMessage)

  const putResourceMutation = usePutResource({
    resourceId: id,
    onSuccess: () => {
      setMessage('Resource has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      void queryClient.invalidateQueries({
        queryKey: ['/admin/resources/{id}', id],
      })
      toggleModalIsOpen()
    },
    onError: () => {
      setMessage('Error updating Resource', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  if (getResourceQuery.isPending) {
    return <CircularProgress />
  }

  if (getResourceQuery.isError) {
    return <p>An error occurred.</p>
  }

  const resource = getResourceQuery.data
  const categoryName =
    getResourceCategoriesQuery.data?.find(
      (category) => category.id === resource.resourceCategoryId
    )?.name ?? 'Uncategorized'

  if (isNil(resource) || !isIdPresent(resource)) {
    return <p>An error occurred.</p>
  }

  return (
    <>
      <SectionHeader
        title="Resource Overview"
        onClickButton={toggleModalIsOpen}
      />
      <Box
        sx={{
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          p: '16px',
          mb: '40px',
        }}
      >
        <Grid container sx={{ rowGap: 2 }} mb={4}>
          <Grid container sx={{ rowGap: 2, columnGap: 2 }}>
            <Grid item md={4}>
              <Typography variant="body-2" color="text.secondary">
                Title
              </Typography>
              <Typography variant="body-1" color="text.primary">
                {resource.title}
              </Typography>
            </Grid>

            <Grid item md={4}>
              <Typography variant="body-2" color="text.secondary">
                Category
              </Typography>
              <Typography variant="body-1" color="text.primary">
                {categoryName}
              </Typography>
            </Grid>

            <Grid item md={3}>
              <Typography variant="body-2" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body-1" color="text.primary">
                {formatDateTime(resource.createdOn, 'P p')}
              </Typography>
            </Grid>
          </Grid>

          <Grid container sx={{ rowGap: 2, columnGap: 2 }}>
            {!resource.fileName ? (
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <Typography variant="body-2" color="text.secondary">
                  URL
                </Typography>
                <Typography
                  variant="body-1"
                  color="text.primary"
                  sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  {resource.url}
                </Typography>
              </Grid>
            ) : null}

            <Grid item xs={12} md={5}>
              <Typography variant="body-2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body-1" color="text.primary">
                {resource.description}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <EditResourceDetails
        resource={resource}
        updateIsActive={putResourceMutation.isPending}
        isModalOpen={isModalOpen}
        onSubmitResource={(resource) => {
          putResourceMutation.mutate(resource)
        }}
        toggleModalIsOpen={toggleModalIsOpen}
      />
    </>
  )
}
